import { Attribute } from '../../decorators/attribute.decorator';
import { Child } from '../../decorators/child.decorator';
import { Component } from '../../decorators/component.decorator';
import { UIElement } from '../ui-element';
import styles from './ui-table.css';

/**
 * @class
 * @memberof SharedComponents
 * @augments {UIElement}
 * @alias UITable
 * @element ui-table
 * @classdesc Represents a class for <code>ui-table</code> element. Wrapper
 * form <b>&lt;table></b> element. Transforms tables for smaller viewports
 * and add different functionality via plugins. For examples see
 * <a href="guidelines-tables.html">tables examples</a>.
 * @property {HTMLTableElement} table Shortcut to table element.
 * @property {boolean} [compact=false] Forces to hide &lt;thead>. Only works with
 * 'sortable' plugin.
 * @param {string} label {@attr label} Label for sorting. Only works with
 * 'sortable' plugin.
 * @property {string} labelAdd {@attr label-add} Label for add row. Only works
 * with 'cloneable' plugin.
 * @property {string} labelRemove {@attr label-remove} Label for remove row. Only
 * works with 'cloneable' plugin.
 * @property {boolean} [rebuildIndexes=false] {@attr rebuild-indexes} Keeps row
 * indexes as a contiguous 0..n sequence when rows are added or removed. Only
 * works with 'cloneable' plugin.
 * @property {UITablePluginType} plugins {@attr plugins}
 * @fires event:tablesort
 * @slot {@type "table"}
 * @example
 * <ui-table>
 *   <table>
 *     <thead>
 *       <tr>
 *         <th>Title 1</th>
 *         <th>Title 2</th>
 *       </tr>
 *     </thead>
 *     <tbody>
 *       <tr>
 *         <td>Cell 1</td>
 *         <td>Cell 2</td>
 *       </tr>
 *     </tbody>
 *   </table>
 * </ui-table>
 */
@Component({ tag: 'ui-table', styles })
export class UITable extends UIElement {
    @Child('table') table!: HTMLTableElement;

    @Attribute(String) label?: string;
    @Attribute(String) labelAdd?: string;
    @Attribute(String) labelRemove?: string;
    @Attribute(Boolean) compact = false;
    @Attribute(Boolean) rebuildIndexes = false;
    @Attribute(String) plugins?: string;

    /* exposed for json and expandable plugins */
    getInstancePlugins() {
        return super.getInstancePlugins();
    }

    /**
     * @inheritDoc
     */
    override render(): void {
        if (!this.table) {
            this.appendChild(
                this.createElement({
                    tagName: 'table',
                })
            );
        }
        this.table.classList.add('table');
    }
}
